/**
 * 계약 품의(117·118) AI 초안 섹션 본문 HTML — 섹션 컨테이너(AiDraftSectionNode)의 자식.
 * 초안 content 는 <b>·<br> 만 쓰는 HTML(그 외 태그는 escape) — <br>·개행을 줄로 보고 줄 단위 문단으로,
 * Lexical 왕복에서 줄 구조가 그대로 남음.
 * 계약서 원본 presigned 링크는 만료되므로 본문에 넣지 않음(조회 슬롯이 최신 accessLink 를 제공)
 */

import { localize } from '@/utils/approval/localize';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const RAW_BOLD_TAG_PATTERN = /<\/?(?:b|strong)>/gi;
const ESCAPED_BOLD_TAG_PATTERN = /&lt;(\/?)(?:b|strong)&gt;/gi;

/** escape 후 굵게 태그만 되살림 — 속성 붙은 태그는 escape 된 채 남음 */
const renderDraftLine = (line: string): string =>
  escapeHtml(line).replace(ESCAPED_BOLD_TAG_PATTERN, '<$1strong>');

export const AI_DRAFT_SECTION_TITLE_PREFIX = '[AI 초안]';

export const buildAiDraftSectionInnerHtml = (
  title: string,
  content: string,
): string => {
  const heading = `<p><strong>${escapeHtml(
    `${localize(AI_DRAFT_SECTION_TITLE_PREFIX)} ${title}`,
  )}</strong></p>`;
  const lines = (content ?? '')
    .replace(/\r\n?/g, '\n')
    .replace(/<br\s*\/?>\n?/gi, '\n')
    .split('\n');
  const body = lines
    .map((line) =>
      line.replace(RAW_BOLD_TAG_PATTERN, '').trim()
        ? `<p>${renderDraftLine(line)}</p>`
        : '<p><br></p>',
    )
    .join('');
  return `${heading}${body}`;
};

const LEGACY_SECTION_START = /<div\b[^>]*\bid="ai_draft_\d+"[^>]*>/gi;
const DIV_TAG = /<(\/?)div\b[^>]*>/gi;
const ESCAPED_BR_PATTERN = /&lt;br\s*\/?&gt;/gi;

/** 여는 div 바로 뒤 위치부터 짝이 맞는 </div> 의 시작 위치. 못 찾으면 문자열 끝 */
const findSectionEnd = (html: string, from: number): number => {
  DIV_TAG.lastIndex = from;
  let depth = 1;
  for (let m = DIV_TAG.exec(html); m; m = DIV_TAG.exec(html)) {
    depth += m[1] ? -1 : 1;
    if (depth === 0) return m.index;
  }
  return html.length;
};

/**
 * 레거시 문서 본문의 AI 초안 섹션(div#ai_draft_{계약 ID}) 안에서 글자로 저장된 <b>·<br> 을 태그로 되돌림(PORTAL-13915).
 * 레거시는 초안을 escape 해 넣어 태그가 글자로 저장됨 — 섹션 밖 본문은 건드리지 않음
 */
export const restoreLegacyAiDraftTags = (html: string): string => {
  if (!html.includes('id="ai_draft_')) return html;
  let result = '';
  let cursor = 0;
  LEGACY_SECTION_START.lastIndex = 0;
  for (
    let m = LEGACY_SECTION_START.exec(html);
    m;
    m = LEGACY_SECTION_START.exec(html)
  ) {
    const bodyStart = m.index + m[0].length;
    const bodyEnd = findSectionEnd(html, bodyStart);
    result +=
      html.slice(cursor, bodyStart) +
      html
        .slice(bodyStart, bodyEnd)
        .replace(ESCAPED_BOLD_TAG_PATTERN, '<$1strong>')
        .replace(ESCAPED_BR_PATTERN, '<br>');
    cursor = bodyEnd;
    LEGACY_SECTION_START.lastIndex = bodyEnd;
  }
  return result + html.slice(cursor);
};
